import React, { forwardRef, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './date.css';
import FormField from '../../common/form-field';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { parseError, periodsDiffInDays } from '../../../services/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';

export default function DatePeriod(
  name: string,
  label: string,
  totalCallback?: Function
): FormComponent {
  return ({ ...rest }) => (
    <DatePeriodComponent
      name={name}
      label={label}
      totalCallback={totalCallback}
      {...rest}
    />
  );
}

//Custom input
const CustomInput = forwardRef((props, ref) => {
  return (
    <label>
      <input {...props} />
      <FontAwesomeIcon icon={faCalendarDay} />
    </label>
  );
});

export function DatePeriodComponent({
  name,
  label,
  totalCallback,
  parentName,
  disabled,
  required,
}: FormComponentProps & {
  name: string;
  label: string;
  totalCallback?: Function;
  required?: string[];
}) {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();
  const dateName = parentName ? `${parentName}.${name}` : name;
  const fromName = `${dateName}.from`;
  const untilName = `${dateName}.until`;
  const fromError = parseError(fromName, errors);
  const untilError = parseError(untilName, errors);

  const { field: from } = useController({
    name: fromName,
    shouldUnregister: false,
  });

  const { field: until } = useController({
    name: untilName,
    shouldUnregister: false,
  });

  const [daysCount, setDaysCount] = useState(
    from.value && until.value
      ? moment(until.value).diff(moment(from.value), 'days') + 1
      : 0
  );

  const refCustomInput = useRef();

  const onDateChange = (
    event: any,
    selectedDate: any,
    isFrom: boolean = true
  ) => {
    const native = event.nativeEvent;
    if (
      !selectedDate ||
      native.data === undefined ||
      (native.srcElement?.defaultValue + native.data).length === 10
    ) {
      const dateObject = selectedDate
        ? new window.Date(selectedDate.toString())
        : null;

      const dateToManage = isFrom ? from : until;
      const opositeDate = isFrom ? until : from;

      //calculate days count between two dates
      const cnt =
        dateObject && opositeDate.value
          ? periodsDiffInDays(
              isFrom ? opositeDate.value : dateObject,
              isFrom ? dateObject : opositeDate.value
            )
          : 0;
      setDaysCount(cnt);

      //if date changed then set callback to calculate total days on all periods
      const convertedDate =
        typeof dateToManage.value === 'string'
          ? new Date(dateToManage.value)
          : dateToManage.value;
      if (totalCallback && dateObject?.getDate() !== convertedDate?.getDate()) {
        totalCallback(name, cnt);
      }

      dateToManage.onChange(
        dateObject
          ? new window.Date(
              window.Date.UTC(
                dateObject.getFullYear(),
                dateObject.getMonth(),
                dateObject.getDate()
              )
            )
          : null
      );
    }
  };

  return (
    <FormField
      labelFor={dateName}
      label={label ? t(label) : t(name)}
      required={required?.includes(dateName)}
      error={[fromError?.message, untilError?.message]}
    >
      <div className="date">
        <div className="react-datepicker-container">
          <DatePicker
            name={fromName}
            disabled={disabled}
            locale="lv-LV"
            dateFormat="dd.MM.yyyy"
            selected={from?.value && new window.Date(from?.value.toString())}
            onChange={(date, event: any) => onDateChange(event, date)}
            customInput={<CustomInput ref={refCustomInput} />}
            autoComplete={'off'}
          />
        </div>
        &nbsp;&nbsp;&nbsp;
        <div className="react-datepicker-container">
          <DatePicker
            name={untilName}
            disabled={disabled}
            locale="lv-LV"
            dateFormat="dd.MM.yyyy"
            selected={until?.value && new window.Date(until?.value.toString())}
            onChange={(date, event: any) => onDateChange(event, date, false)}
            customInput={<CustomInput ref={refCustomInput} />}
            autoComplete={'off'}
          />
        </div>
        <label className="label">{daysCount + ' ' + t('days')}</label>
      </div>
    </FormField>
  );
}
